<template>
  <div class="client-general">
    <v-row>
      <v-col cols="12">
        <div>ce-api-uri <span class="text-red">*</span></div>
        <v-select
          id="ce-environment-selection"
          v-model="ceEnvironmentSelection"
          class="type-dropdown excursion-selection"
          item-title="ceApiUri"
          :items="ceEnvironments"
          return-object
          variant="solo"
          density="compact"
          placeholder="Select Content Engine environment"
          clearable
          clear-icon="mdi-close"
          required
          :error-messages="ceApiUriErrors"
          @blur="v$.ceEnvironmentSelection.$touch()"
          @update:model-value="onChangeCeApiUri"
        >
          <template #item="data">
            <v-list-item
              v-bind="data.props"
              :id="`client-individual-modal--ce-api-uri--option-${
                String(data.item.title).split('//')[1].split('.')[0]
              }`"
            />
          </template>
        </v-select>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12">
        <div>x-site-uri <span class="text-red">*</span></div>
        <v-text-field
          id="txtXSiteUri"
          v-model="contentEngine.xSiteUri"
          variant="solo"
          density="compact"
          placeholder="x-site-uri"
          class="name-textbox excursion-input"
          required
          :error-messages="ceXSiteUriErrors"
          @keyup="autoPopulatexSiteUrl()"
          @update:model-value="onContentEngineChanged"
        />
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12">
        <div>module-name <span class="text-red">*</span></div>
        <v-text-field
          id="txtModuleName"
          v-model="contentEngine.moduleName"
          variant="solo"
          density="compact"
          placeholder="module-name"
          class="name-textbox excursion-input"
          required
          :error-messages="ceModuleNameErrors"
          @update:model-value="onContentEngineChanged"
        />
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="12" class="d-flex justify-end align-center">
        <cs-button
          v-if="clearModuleCacheEnabled"
          id="btnPrevDeleteCache"
          primary
          :disabled="clearModuleCacheButtonDisabled"
          label="Clear Module Cache"
          @click="prevDeleteCacheDialog = true"
        ></cs-button>
        <cs-button
          v-if="editMode === 'update'"
          id="btnSaveContentEngine"
          primary
          label="Save Content Engine"
          :disabled="saveButtonDisabled"
          :loading="saveActionInProgress"
          @click="showConfirmSaveDialog = true"
        ></cs-button>
      </v-col>
    </v-row>
    <cs-form-dialog
      id="dlg-client-content-engine-clear-cache"
      v-model="prevDeleteCacheDialog"
      body-id="dlg-client-content-engine-clear-cache-body"
      heading="Client Individual Editor"
      :primary-action="{
        label: 'Yes',
        eventName: 'primary-click',
        loading: cacheClearActionInProgress
      }"
      :secondary-action1="{ label: 'Cancel', eventName: 'secondary1-click' }"
      @primary-click="deleteRiskCaches"
      @secondary1-click="prevDeleteCacheDialog = false"
    >
      <template #cs-form-dialog-content>
        Clearing the cache may affect other clients using the same module. Are you sure you want to
        clear the cache?
      </template>
    </cs-form-dialog>
    <cs-form-dialog
      id="dlg-client-ind-editor-clear-cache-result"
      v-model="showNoticeDialog"
      body-id="dlg-client-ind-editor-clear-cache-result-body"
      heading="Client Individual Editor"
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="onDialogOkBtn"
    >
      <template #cs-form-dialog-content>
        <div v-safe-html="resultMessage"></div>
        <v-spacer></v-spacer>
        <div v-safe-html="warningMessage" class="warning-message text-red-darken-2"></div>
      </template>
    </cs-form-dialog>
    <cs-form-dialog
      id="dlg-client-content-engine-confirm-cache"
      v-model="showConfirmSaveDialog"
      body-id="dlg-client-content-engine-confirm-cache-body"
      heading="Client Individual Editor"
      :primary-action="{
        label: 'Continue',
        eventName: 'primary-click',
        loading: saveActionInProgress
      }"
      :secondary-action1="{ label: 'Cancel', eventName: 'secondary1-click' }"
      @primary-click="onSave()"
      @secondary1-click="showConfirmSaveDialog = false"
    >
      <template #cs-form-dialog-content>
        You are about to save the content engine settings. Are you sure you want to proceed?
      </template>
    </cs-form-dialog>
  </div>
</template>

<script>
import { CSBase } from '@complispace/cs-design-system';
import { required } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import * as types from '@/store/mutationTypes';
import { useVuelidate } from '@vuelidate/core';

export default {
  name: 'ClientContentEngine',

  extends: CSBase,

  props: {
    clientSid: { type: String, required: false, default: '' },
    subdomain: { type: String, required: false, default: '' },
    // @TODO: temporary here as the save action is done in the parent component
    clientSaveActionInProgress: { type: Boolean, required: false, default: false }
  },

  emits: ['contentEngineChanged', 'contentEngineSaved'],

  setup: () => ({ v$: useVuelidate() }),

  data() {
    return {
      editMode: { type: String, required: false, default: 'create' },
      contentEngine: {
        ceApiUri: '',
        xSiteUri: '',
        moduleName: ''
      },
      ceEnvironmentSelection: null,
      prevDeleteCacheDialog: false,
      showNoticeDialog: false,
      resultMessage: '',
      warningMessage: '',
      cacheClearActionInProgress: false,
      showConfirmSaveDialog: false,
      saveActionInProgress: false,
      isSaved: false
    };
  },

  computed: {
    ...mapGetters({
      clientContentEngineView: 'clients/clientContentEngineView',
      clearModuleCacheEnabled: 'risks/clearModuleCacheEnabled',
      ceApiUris: 'risks/ceEnvironment'
    }),

    ceEnvironments() {
      const environments = [];
      if (!this.ceApiUris) {
        return environments;
      }
      Object.keys(this.ceApiUris).forEach((key) => {
        environments.push(this.ceApiUris[key]);
      });
      return environments;
    },

    ceApiUriErrors() {
      const errors = [];
      if (!this.v$.ceEnvironmentSelection.$dirty) return errors;
      if (this.v$.ceEnvironmentSelection.ceApiUri.required.$invalid) {
        errors.push('ce-api-uri is required');
      }
      return errors;
    },

    ceXSiteUriErrors() {
      const errors = [];
      if (!this.v$.contentEngine.$dirty) return errors;
      if (this.v$.contentEngine.xSiteUri.required.$invalid) {
        errors.push('x-site-uri is required');
      }
      return errors;
    },

    ceModuleNameErrors() {
      const errors = [];
      if (!this.v$.contentEngine.$dirty) return errors;
      if (this.v$.contentEngine.moduleName.required.$invalid) {
        errors.push('module-name is required');
      }
      return errors;
    },

    clearModuleCacheButtonDisabled() {
      let disabled = false;
      if (
        !this.contentEngine ||
        !this.contentEngine.xSiteUri ||
        !this.contentEngine.moduleName ||
        this.cacheClearActionInProgress ||
        this.clientSaveActionInProgress ||
        this.hasChanges
      ) {
        disabled = true;
      }
      return disabled;
    },

    hasChanges() {
      const { ceApiUri, xSiteUri, moduleName } = this.clientContentEngineView;
      if (
        this.contentEngine.ceApiUri !== ceApiUri ||
        this.contentEngine.xSiteUri !== xSiteUri ||
        this.contentEngine.moduleName !== moduleName
      ) {
        return true;
      }
      return false;
    },

    saveButtonDisabled() {
      let disabled = false;
      if (
        !this.contentEngine ||
        !this.contentEngine.ceApiUri ||
        !this.contentEngine.xSiteUri ||
        !this.contentEngine.moduleName ||
        this.cacheClearActionInProgress ||
        this.clientSaveActionInProgress
      ) {
        disabled = true;
      }
      return disabled;
    }
  },

  watch: {
    async clientSid() {
      this.initPage(this.clientSid);
    },

    clientContentEngineView() {
      this.reloadData();
    },

    contentEngine: {
      handler() {
        this.$emit('contentEngineChanged', this.contentEngine);
      },
      deep: true
    },

    showNoticeDialog(boolShown) {
      if (!boolShown) {
        this.resetDeleteCacheErrors();
      }
    }
  },

  validations: {
    ceEnvironmentSelection: {
      ceApiUri: {
        required
      }
    },
    contentEngine: {
      xSiteUri: {
        required
      },
      moduleName: {
        required
      }
    }
  },

  created() {},

  async mounted() {
    this.initPage(this.clientSid);
  },

  methods: {
    async initPage(clientSid) {
      if (clientSid !== '') {
        this.editMode = 'update';
        await this.fetchClientContentEngine();
        this.reloadData();
      }
      if (!this.ceApiUris || Object.keys(this.ceApiUris).length === 0) {
        await this.$store.dispatch('app/fetchCeApiUris');
      }
    },

    async fetchClientContentEngine() {
      try {
        if (this.clientSid) {
          this.setLoading(true);
          await this.$store.dispatch('clients/fetchClientContentEngine', this.clientSid);
          this.clearLoading();
        }
      } catch (e) {
        this.clearLoading();
      }
    },

    onChangeCeApiUri() {
      if (this.ceEnvironmentSelection) {
        this.contentEngine.ceApiUri = this.ceEnvironmentSelection.ceApiUri;

        this.autoPopulatexSiteUrl(true);
      } else {
        this.contentEngine.ceApiUri = '';
      }
      this.onContentEngineChanged();
    },

    autoPopulatexSiteUrl(forceChanged) {
      if (
        (forceChanged || !this.contentEngine.xSiteUri) &&
        this.subdomain &&
        this.ceEnvironmentSelection
      ) {
        const selectedCeEnvironment = this.getSelectedCeEnvironment();
        if (selectedCeEnvironment) {
          if (selectedCeEnvironment === 'production') {
            this.contentEngine.xSiteUri = `https://${this.subdomain}.app.safetripbuilder.com`;
          } else {
            this.contentEngine.xSiteUri = `https://${this.subdomain}.app-${selectedCeEnvironment}.safetripbuilder.com`;
          }
        }
        this.contentEngine = JSON.parse(JSON.stringify(this.contentEngine));
      }
    },

    getSelectedCeEnvironment() {
      let environment = '';
      if (this.ceApiUris) {
        Object.keys(this.ceApiUris).forEach((key) => {
          if (this.ceEnvironmentSelection.ceApiUri === this.ceApiUris[key].ceApiUri) {
            environment = key;
          }
        });
      }
      return environment;
    },

    onDialogOkBtn() {
      this.showNoticeDialog = false;
      this.cacheClearActionInProgress = false;
      this.warningMessage = '';
      this.resultMessage = '';
    },

    reloadData() {
      this.clear();
      const { ceApiUri, xSiteUri, moduleName } = this.clientContentEngineView;
      this.contentEngine = { ceApiUri, xSiteUri, moduleName };
      this.ceEnvironmentSelection = null;
      if (ceApiUri) {
        let selectedCeApiUri = ceApiUri;
        if (ceApiUri.substring(ceApiUri.length - 1) === '/') {
          selectedCeApiUri = ceApiUri.substring(0, ceApiUri.lastIndexOf('/'));
        }
        this.ceEnvironmentSelection = {
          ceApiUri: selectedCeApiUri
        };
      }
    },

    clear() {
      this.contentEngine = {
        clientId: '',
        ceApiUri: '',
        xSiteUri: '',
        moduleName: ''
      };
    },

    onContentEngineChanged() {
      this.v$.contentEngine.$touch();
      this.emitContentEngineChanged();
    },

    emitContentEngineChanged() {
      this.$emit('contentEngineChanged', this.contentEngine);
    },

    async deleteRiskCaches() {
      this.$store.commit('app/setSilentAxiosError', true);
      try {
        this.cacheClearActionInProgress = true;
        const response = await this.$store.dispatch('risks/deleteRiskCaches', this.contentEngine);

        this.prevDeleteCacheDialog = false;
        this.cacheClearActionInProgress = false;
        if (response && !response.error && response.data && response.data.isCleared) {
          this.alertClearCacheResult('success', '', '');
        } else {
          this.handleDeleteRiskCachesResult(response);
        }
      } catch (e) {
        this.resetDeleteCacheErrors();
      }
    },

    alertClearCacheResult(status, errorMessage, warningMessage) {
      if (status === 'success' && !errorMessage) {
        this.resultMessage = 'Content Cache has successfully been cleared.';
        this.showNoticeDialog = true;
        return;
      }
      if (status === 'success' && !errorMessage) {
        this.resultMessage = errorMessage;
        this.showNoticeDialog = true;
        return;
      }

      // failed
      this.resultMessage = errorMessage || `Failed to clear cache.`;
      this.warningMessage = warningMessage || '';

      this.showNoticeDialog = true;
    },

    handleDeleteRiskCachesResult(response) {
      this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
      const deleteCacheErrorMessage =
        'Oops, Content Cache cannot be cleared. (It might be cleared already or please contact support for help)';

      if (response && response.data.isCleared === false && !response.error) {
        this.alertClearCacheResult('success', response.data.message || deleteCacheErrorMessage);
        return;
      }

      let warningMessage = '';

      if (!response.error) return;
      const error = response.data;
      try {
        const errorMessages = error.message?.split(':') || [];
        warningMessage = `errorRefId: ${error.sentinelEventSid || errorMessages[0]}`;
        this.alertClearCacheResult('error', `Failed to clear cache.`, warningMessage);
      } catch {
        this.resetDeleteCacheErrors();
      }
    },

    resetDeleteCacheErrors() {
      this.$store.commit('app/setSilentAxiosError', false);
      this.prevDeleteCacheDialog = false;
      this.cacheClearActionInProgress = false;
      this.warningMessage = '';
    },

    async onSave() {
      this.saveActionInProgress = true;
      try {
        this.$store.commit('app/setSilentAxiosError', true);
        const saveRes = await this.saveContentEngine();
        if (!saveRes || saveRes.status !== 'success') {
          this.saveActionInProgress = false;
          this.handleSaveError(saveRes.error);
          return;
        }
        this.alertSaveResult('success');
        this.$emit('contentEngineSaved', true);
      } catch (e) {
        this.handleSaveError(e);
      } finally {
        this.saveActionInProgress = false;
      }
    },

    async saveContentEngine() {
      const contentEngine = { ...this.contentEngine };
      contentEngine.clientSid = this.clientSid;

      const patchRes = await this.$store.dispatch(
        'clients/patchClientContentEngine',
        contentEngine
      );

      return patchRes;
    },

    alertSaveResult(status, errorMessage, warningMessage) {
      this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
      this.warningMessage = '';
      if (status === 'success') {
        // success
        this.resultMessage = 'Content Engine Setting has been saved.';
        this.warningMessage = warningMessage || '';
        this.isSaved = true;
      } else {
        // failed
        this.isSaved = false;
        this.resultMessage = errorMessage || `Failed to save Client information.`;
        this.warningMessage = warningMessage || '';
      }
      this.showConfirmSaveDialog = false;
      this.showNoticeDialog = true;
    },

    handleSaveError(error) {
      let displayMessage = error.message || '';
      let warningMessage = '';
      try {
        const errorMessages = error.message?.split(':') || [];
        if (errorMessages.length > 3) {
          // eslint-disable-next-line prefer-destructuring
          displayMessage = `Save Error: ${errorMessages[2]}`;
          warningMessage = `errorRefId: ${errorMessages[1]}`;
        }
        this.alertSaveResult(
          'error',
          `Failed to save Client information. <br/> ${displayMessage}`,
          warningMessage
        );
      } finally {
        this.$store.commit('app/setSilentAxiosError', true);
      }
    }
  }
};
</script>
<style scoped>
.warning-message {
  font-size: 10pt;
  padding-top: 12px;
  font-style: italic;
}
</style>
